import React, { useState } from "react";
import { Formik, Form, Field } from "formik";
import { Input, Button, message, Typography } from "antd";
import { API_BASE_URL } from "../config/apiConfig";
import { useTranslation } from "react-i18next";
import HomeButton from "../components/HomeButton";

const { TextArea } = Input;
const { Title } = Typography;

const QuestionAnswering: React.FC = () => {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleQuestionAnswering = async (values: {
    inputText: string;
    question: string;
  }) => {
    setLoading(true);
    setAnswer("");
    const token = localStorage.getItem("token");

    try {
      const response = await fetch(`${API_BASE_URL}/zhadiger/api/qa/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "text/event-stream",
          Authorization: `${token}`,
          "ngrok-skip-browser-warning": "true",
        },
        body: JSON.stringify({
          texts: values.inputText,
          question: values.question,
        }),
      });

      if (!response.body) {
        throw new Error("No response body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data:")) {
            const content = line.substring(5);
            setAnswer((prev) => prev + content);
          }
        }
      }

      message.success("Answer has been generated!");
    } catch (error) {
      console.error("Streaming error:", error);
      message.error("Error generating answer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4 ml-4 pt-4 ">
        <HomeButton />
      </div>
      <div className="max-w-xl mx-auto mt-10 p-6 bg-white border border-gray-200 rounded-lg shadow-md flex-grow">
        <Title level={2} className="text-center font-bold text-gray-700">
          {t("qa")}
        </Title>

        <Formik
          initialValues={{ inputText: "", question: "" }}
          onSubmit={handleQuestionAnswering}
        >
          {({ handleSubmit }) => (
            <Form className="space-y-4">
              <Field name="inputText">
                {({ field }: { field: any }) => (
                  <TextArea
                    {...field}
                    placeholder={t("enter_the_text") || "Enter text"}
                    rows={4}
                    disabled={loading}
                    className="p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </Field>

              <Field name="question">
                {({ field }: { field: any }) => (
                  <Input
                    {...field}
                    placeholder={t("enter_question") || "Enter question"}
                    disabled={loading}
                    className="p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </Field>

              <Button
                type="primary"
                onClick={() => handleSubmit()}
                loading={loading}
                className="w-full bg-blue-500 text-white hover:bg-blue-600 rounded-lg py-2 transition duration-200"
              >
                {t("get_answer") || "Get Answer"}
              </Button>
            </Form>
          )}
        </Formik>

        {answer && (
          <div className="mt-6 text-center">
            <Title level={4} className="text-gray-600">
              {t("answer") || "Answer"}
            </Title>
            <div className="p-4 bg-gray-100 border border-gray-300 rounded-lg whitespace-pre-wrap text-left">
              {answer}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QuestionAnswering;
